import type { TFunction } from 'i18next';
import {
  Calendar,
  CalendarClock,
  Download,
  Folder,
  Image,
  Layers,
  ListVideo,
  type LucideIcon,
  Monitor,
  Pencil,
  Plus,
  Rss,
  Settings2,
  Smartphone,
  Terminal,
  User,
  Users2,
} from 'lucide-react';
import { GROUP_TYPES, ROOT_TEAM_ID, type GroupType } from '@scio3/shared-types';

// Shared between the top-nav global-search dropdown (`GlobalSearchBar`) and the
// full search-results page (`SearchPage`). Pure data + display helpers only —
// JSX and Apollo hooks live with their respective consumers so the two surfaces
// can diverge visually without stepping on each other.

/** Below this length the search hooks return empty — matches the input gates
 *  in both the dropdown and the results page. */
export const MIN_QUERY_LENGTH = 2;
/** HTML `maxLength` cap on both search inputs. Blocks pasting mega-strings
 *  that would blow up the backend Atlas query. */
export const MAX_QUERY_LENGTH = 100;

export const ENTITY_ROUTES: Record<string, string> = {
  screens: '/app/screenManagement',
  assets: '/app/assetManagement',
  playlists: '/app/playlistManagement',
  schedules: '/app/scheduleManagement',
  operationalSchedules: '/app/s/operational-schedule',
  engages: '/app/engageManagement',
};

// Maps search entity types to `usePermissions` entity names for `canEdit()` checks.
// Only the 5 primary content types map — folders / feeds / teams / members go
// through their own dedicated select handlers with different permission rules.
// The union mirrors `Entity` from `@/hooks/usePermissions` (not exported).
export const ENTITY_PERMISSION_MAP: Record<string, 'screen' | 'asset' | 'playlist' | 'schedule'> = {
  screens: 'screen',
  assets: 'asset',
  playlists: 'playlist',
  schedules: 'schedule',
  engages: 'asset', // Engage uses asset permissions
};

// Order the sidebar / dropdown iterates when rendering sections. Changing this
// list picks up automatically in both the dropdown and the page.
export const GROUP_ORDER = [
  'screens',
  'assets',
  'playlists',
  'schedules',
  'folders',
  'feeds',
  'engages',
  'operationalSchedules',
  'teams',
  'members',
] as const;

export type SearchEntityType = (typeof GROUP_ORDER)[number];

/**
 * Search-page filter-rail grouping — matches the design mockup's split.
 * PRIMARY is the "FILTER BY TYPE" group (main content types users typically
 * think of first). SECONDARY is the "OTHER MATCHES" group (folders, feeds,
 * teams, members — matching modes for the same query).
 *
 * Together these MUST cover every value in `GROUP_ORDER` — a compile-time
 * check at the bottom of this file enforces the invariant. Adding a new
 * value to `GROUP_ORDER` without placing it in one of these two arrays
 * fails typecheck.
 */
export const PRIMARY_ENTITY_TYPES = [
  'screens',
  'assets',
  'playlists',
  'schedules',
  'engages',
  'operationalSchedules',
] as const;

export const SECONDARY_ENTITY_TYPES = ['folders', 'feeds', 'teams', 'members'] as const;

/**
 * Entity types temporarily hidden from every search UI surface (dropdown
 * group, results-page section, filter rail row, URL `?type=` gate).
 *
 * Kept in `GROUP_ORDER` and the `GLOBAL_SEARCH` GraphQL include list so the
 * backend contract, TypeScript response types, and existing tests remain
 * unchanged — the hide is UI-only. To re-enable a type: remove it from
 * this list. No other changes needed.
 */
export const HIDDEN_ENTITY_TYPES: readonly SearchEntityType[] = ['feeds'];

/** True when the entity type should render in the search UI. */
export function isEntityTypeVisible(type: SearchEntityType): boolean {
  return !HIDDEN_ENTITY_TYPES.includes(type);
}

// Compile-time check: every GROUP_ORDER value must land in PRIMARY or
// SECONDARY. If you add to GROUP_ORDER and forget to categorize, `_coverage`
// stops being assignable to `true` and typecheck fails.
type _CoverageCheck =
  Exclude<
    SearchEntityType,
    (typeof PRIMARY_ENTITY_TYPES)[number] | (typeof SECONDARY_ENTITY_TYPES)[number]
  > extends never
    ? true
    : never;
const _coverage: _CoverageCheck = true;
void _coverage; // silence unused-var lint; the value exists purely for type-check

/**
 * Lucide icon component per entity type. Both the dropdown and the filter
 * rail render these — using the same map keeps the visual language
 * consistent across surfaces. Callers control sizing/color via className.
 */
export const ENTITY_ICONS: Record<SearchEntityType, LucideIcon> = {
  screens: Monitor,
  assets: Image,
  playlists: ListVideo,
  schedules: Calendar,
  operationalSchedules: CalendarClock,
  folders: Folder,
  feeds: Rss,
  engages: Smartphone,
  teams: Users2,
  members: User,
};

/**
 * Icons for quick-nav ACTION rows, keyed by the entry's `openDialog` slug
 * (`nav-targets.ts`). Only the non-create actions need an entry — everything
 * else is a create/add and falls through to `Plus`, so adding a new create
 * action needs no change here.
 */
const ACTION_ICONS: Record<string, LucideIcon> = {
  'screen-preconfigure': Settings2,
  'screen-execute': Terminal,
  'screen-export-devices-list': Download,
  'asset-feeds': Rss,
  'schedule-global-default': Settings2,
  'account-manage-roles': Settings2,
  'account-manage-members': Users2,
  'account-update-team': Pencil,
};

/** Row icon for an action entry. `Plus` is the create/add default. */
export function getActionIcon(openDialog: string | undefined): LucideIcon {
  return (openDialog && ACTION_ICONS[openDialog]) || Plus;
}

/**
 * "All" bucket icon for the filter rail's default (no-type-filter) row.
 */
export const ALL_ENTITIES_ICON: LucideIcon = Layers;

/**
 * Sort options in the SORT BY section of the filter rail. URL-driven via
 * `?sort=`. Missing param = 'relevance' (the default, keeps backend order).
 */
export const SORT_MODES = ['relevance', 'name', 'lastModified'] as const;

export type SearchSortMode = (typeof SORT_MODES)[number];

/**
 * Sort direction. URL-driven via `?dir=`. Only serialized when it differs
 * from the mode's natural default (`getDefaultSortDirection`) so shared
 * URLs stay clean — `?sort=name` implies ascending; adding
 * `?sort=name&dir=desc` overrides to descending.
 */
export type SearchSortDirection = 'asc' | 'desc';

/**
 * Direction a mode uses when the user activates it without an explicit
 * `dir` param. Follows the numeric asc/desc convention (asc = low value →
 * high value, desc = high value → low value):
 *   - `name` → asc (A → Z; A is "low")
 *   - `lastModified` → desc (newest first; newer = higher timestamp)
 *   - `relevance` → desc (most relevant first; higher score = more relevant)
 * `asc` on relevance reverses the backend order to surface the weakest
 * matches first — practically useful only for debugging low-score hits, but
 * the toggle exists for UI consistency.
 */
export function getDefaultSortDirection(mode: SearchSortMode): SearchSortDirection {
  return mode === 'name' ? 'asc' : 'desc';
}

/**
 * Human labels for the SORT BY options, wrapped through the caller's `t()`.
 */
export function getSortModeLabels(t: TFunction): Record<SearchSortMode, string> {
  return {
    // "Best matches", not "Relevance": relevance is the ranking CONCEPT, and
    // as a menu entry it reads like a metric you could inspect. "Best matches"
    // says what the option gives you.
    relevance: t('Best matches'),
    name: t('Name'),
    // Matches the sort options in `SearchFilterBar` and the "Updated Date"
    // wording every other list page uses. The `lastModified` KEY stays — it is
    // the API field name, not something the user reads.
    lastModified: t('Updated Date'),
  };
}

/**
 * Returns the full label map for search entity types, wrapped through the
 * caller's `t()`. Used by both the dropdown and the search page filter rail
 * so labels stay consistent across surfaces.
 */
export function getGroupLabels(t: TFunction): Record<SearchEntityType, string> {
  return {
    screens: t('Screens'),
    assets: t('Files/Assets'),
    playlists: t('Playlists'),
    schedules: t('Schedules'),
    operationalSchedules: t('Operational Schedules'),
    folders: t('Folders'),
    feeds: t('Feeds'),
    engages: t('Engage'),
    teams: t('Teams'),
    members: t('Members'),
  };
}

// Folder rows show a "{Type} Folder" subtitle. Each entry is a full i18n key
// so translators own the entire noun phrase (word order + gender agreement in
// French/Spanish, no-space-composition in Japanese, etc.) instead of the
// runtime concatenating two translated segments. Keyed by `GroupType` values
// from shared-types so any future group-type addition surfaces at compile time.
export const FOLDER_TYPE_LABELS: Record<GroupType, string> = {
  [GROUP_TYPES.DEVICE]: 'Screen Folder',
  [GROUP_TYPES.ASSET]: 'Asset Folder',
  [GROUP_TYPES.PLAYLIST]: 'Playlist Folder',
  [GROUP_TYPES.SCHEDULE]: 'Schedule Folder',
  [GROUP_TYPES.ENGAGE]: 'Engage Folder',
};

// Maps a folder's `type` (a `GroupType` value) to the destination feature page
// route. Used by the folder-select handler.
export const FOLDER_TYPE_ROUTES: Record<GroupType, string> = {
  [GROUP_TYPES.DEVICE]: ENTITY_ROUTES.screens,
  [GROUP_TYPES.ASSET]: ENTITY_ROUTES.assets,
  [GROUP_TYPES.PLAYLIST]: ENTITY_ROUTES.playlists,
  [GROUP_TYPES.SCHEDULE]: ENTITY_ROUTES.schedules,
  [GROUP_TYPES.ENGAGE]: ENTITY_ROUTES.engages,
};

/**
 * Detects the entity type from a pathname. Used by the dropdown to bubble the
 * current-page's entity type to the top of the section order, so users see
 * "Screens" first when they're on the Screens page.
 */
export function getCurrentEntityType(pathname: string): string | null {
  if (pathname.startsWith(ENTITY_ROUTES.screens)) return 'screens';
  if (pathname.startsWith(ENTITY_ROUTES.assets)) return 'assets';
  if (pathname.startsWith(ENTITY_ROUTES.playlists)) return 'playlists';
  if (pathname.startsWith(ENTITY_ROUTES.schedules)) return 'schedules';
  if (pathname.startsWith(ENTITY_ROUTES.engages)) return 'engages';
  return null;
}

/**
 * Members repeat per team-pairing so `_id` alone isn't unique — a single user
 * in three teams produces three rows sharing an `_id`. Key includes the
 * team-scope to keep React happy.
 */
export function getRowKey(
  entityType: string,
  item: Record<string, unknown>,
  index: number,
): string {
  if (entityType === 'members') {
    const uid = (item.userId as string) || (item.email as string) || String(index);
    return `${item.teamId as string}-${uid}`;
  }
  return item._id as string;
}

/**
 * Best-available display name per entity type. Consumer passes their
 * `useTranslation().t` so the "Untitled"/"Unknown"/"Default Team" fallbacks
 * localize correctly.
 */
export function getItemName(
  entityType: string,
  item: Record<string, unknown>,
  t: TFunction,
): string {
  if (entityType === 'screens') {
    return (item.deviceName as string) || (item.name as string) || (item._id as string);
  }
  if (entityType === 'assets' || entityType === 'engages') {
    return (
      (item.originalFileName as string) ||
      (item.appType as string) ||
      (item.name as string) ||
      t('Untitled')
    );
  }
  if (entityType === 'members') {
    return (item.name as string) || (item.email as string) || t('Unknown');
  }
  // Synthetic Default Team row — backend returns hardcoded English 'Default Team';
  // localize for display so a Spanish user sees "Equipo predeterminado".
  if (entityType === 'teams' && item._id === ROOT_TEAM_ID) {
    return t('Default Team');
  }
  return (item.name as string) || (item._id as string);
}

/**
 * Folder rows carry a "{Type} Folder" subtitle. Returns null if the folder's
 * type isn't one of the 5 primary `GroupType` values.
 */
export function getFolderSubtitle(item: Record<string, unknown>, t: TFunction): string | null {
  const type = item.type as string | undefined;
  if (!type) return null;
  const label = FOLDER_TYPE_LABELS[type.toLowerCase() as GroupType];
  return label ? t(label) : null;
}
